import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Form, Row, Col, theme, FormInstance } from 'antd';
import './styles.scoped.css';

const EDIT_MODES = {
  ADDING: 'adding',
  REMOVING: 'removing',
  NULL: null,
};

function Box(props: any) {
  return (
    <div
      className={classNames('box', { active: props.active })}
      role="switch"
      // onMouseEnter={props.onMouseEnter}
      onMouseDown={props.onMouseDown}
      aria-checked={props.active}
      tabIndex={-1}
      style={{
        backgroundColor: props.active
          ? props.token.colorPrimary
          : props.token.colorBgContainerDisabled,
        ...(props.active && { color: 'white' }),
        borderColor: props.active ? props.token.colorPrimary : 'white',
      }}
    >
      {props.item}
    </div>
  );
}

// Children of Form.Item receives value and onChange
// value: field gets directly set from Form
// onChange: call this to update Form field
function BoxRow(props: any) {
  const attachListener = !(props.viewMode || props.disabled);
  return (
    <div
      role="grid"
      className={classNames('box-row', { view: props.viewMode, disabled: props.disabled })}
      // onMouseLeave={attachListener ? () => props.stopEdit(props.onChange) : undefined}
      // onMouseUp={attachListener ? () => props.stopEdit(props.onChange) : undefined}
      tabIndex={-1}
      style={{ backgroundColor: props.token.colorBgBase }}
    >
      {props.itemList.map((item: any) => (
        <Box
          key={`${props.keyPrefix}-${item}`}
          onMouseEnter={attachListener ? () => props.onBoxEnter(item) : undefined}
          onMouseDown={attachListener ? () => props.onBoxDown(item) : undefined}
          item={item}
          disabled={props.disabled}
          active={props.activeList?.includes(item)}
          token={props.token}
        />
      ))}
    </div>
  );
}

type ToggleState = {
  activeList: any[];
  editMode: string | null;
  initialValues: any[];
};

interface ToggleSliderProps {
  name: string | string[];
  disabled: boolean;
  label: string;
  itemList: any[];
  initialValues: any[];
  viewMode: boolean;
  getForm: () => FormInstance<any>;
}

function ToggleSlider(props: ToggleSliderProps) {
  const {
    name,
    label,
    itemList = ['A', 'B', 'C'],
    initialValues: defaultValues = [],
    disabled = false,
    viewMode = false,
    getForm,
  } = props;

  const { token } = theme.useToken();

  const [customState, setCustomState] = useState<ToggleState | null>({
    activeList: defaultValues,
    editMode: EDIT_MODES.NULL,
    initialValues: defaultValues,
  });

  // const getDerivedStateFromProps = (nextProps, prevState) => {
  //   if (isEqual(nextProps.initialValues, prevState.initialValues)) return null;
  //   return {
  //     initialValues: nextProps.initialValues,
  //     activeList: nextProps.initialValues || [],
  //     editMode: EDIT_MODES.NULL,
  //   };
  // };

  const stopEdit = useCallback((onChange: any) => {
    let activeList: any[] = [];
    let editsWereMade = false;
    setCustomState((currState) => {
      if (!currState?.editMode) {
        return null;
      }
      activeList = currState.activeList;
      editsWereMade = true;
      return { ...currState, editMode: EDIT_MODES.NULL };
    });
    if (editsWereMade) onChange(activeList);
  }, []);

  const changeBoxState = useCallback(
    (item: any) => {
      let newState: any[] = [];

      setCustomState((currState) => {
        if (currState?.editMode === EDIT_MODES.ADDING) {
          const i = currState?.activeList?.indexOf(item);
          if (i === -1 || i === undefined) {
            newState = [...(currState?.activeList || []), item];
            getForm?.().setFieldValue(name, newState);
            return { ...currState, activeList: newState };
          }
        }
        else if (currState?.editMode === EDIT_MODES.REMOVING) {
          const i = currState?.activeList.indexOf(item);
          if (i !== -1 && i !== undefined) {
            newState = [...(currState?.activeList || [])];
            newState.splice(i, 1);
            getForm?.().setFieldValue(name, newState);
            return { ...currState, activeList: newState };
          }
        }
        return null;
      });
    },
    [name, getForm],
  );

  const boxDown = useCallback(
    (item: any) => {
      // @ts-ignore
      setCustomState((currState) => {
        if (currState?.activeList?.includes(item))
          return { ...currState, editMode: EDIT_MODES.REMOVING };
        return { ...currState, editMode: EDIT_MODES.ADDING };
      });
      changeBoxState(item);
    },
    [changeBoxState],
  );

  const boxEnter = useCallback(
    (item: any) => {
      if (customState?.editMode) changeBoxState(item);
    },
    [changeBoxState, customState?.editMode],
  );

  if (viewMode) {
    return (
      <Row style={{ display: 'flex', flexDirection: 'row', marginBottom: 0 }}>
        <Col span={3}>{label}</Col>
        <Col>
          <BoxRow
            keyPrefix={label}
            itemList={itemList}
            activeList={customState?.activeList}
            viewMode
            token={token}
          />
        </Col>
      </Row>
    );
  }

  return (
    <Form.Item name={name} label={label}>
      <BoxRow
        keyPrefix={label}
        itemList={itemList}
        activeList={customState?.activeList}
        disabled={disabled}
        onBoxEnter={boxEnter}
        onBoxDown={boxDown}
        stopEdit={stopEdit}
        token={token}
        // @ts-ignore
        onChange={props.onChange}
      />
    </Form.Item>
  );
}

export default ToggleSlider;
